/**
 * HidProvider — reálné PUSH čtení z DYMO Digital Postal Scale M5 přes WebHID.
 * Protokol viz dymoParser.js. VID 0x0922, filtr bez pevného PID (M-řada sdílí protokol).
 *
 * Lifecycle:
 *   tryReconnect() — tichý reconnect na dříve povolené zařízení (bez promptu)
 *   connect()      — systémový prompt requestDevice() (vyžaduje uživatelské gesto)
 *   start()/stop() — (od)registrace inputreport listeneru
 *   disconnect()   — close + úklid
 */
import { InstrumentProvider, ConnectionState } from './baseProvider';
import { parseDymoReport, DYMO_VENDOR_ID } from './dymoParser';

export function isWebHidAvailable() {
  return typeof navigator !== 'undefined'
    && 'hid' in navigator
    && typeof window !== 'undefined'
    && window.isSecureContext === true;
}

export class HidProvider extends InstrumentProvider {
  constructor() {
    super();
    this._device = null;
    this._lastError = null;
    this._onInputReport = this._handleInputReport.bind(this);
    this._onDisconnect = this._handleDisconnect.bind(this);
  }

  get name() {
    return this._device && this._device.productName ? this._device.productName : 'DYMO M5 (HID)';
  }

  get lastError() { return this._lastError; }

  /** Identifikace pro provenance/audit (zachytí se po připojení). */
  get identity() {
    if (!this._device) return null;
    return {
      vendorId: this._device.vendorId,
      productId: this._device.productId,
      productName: this._device.productName || null,
    };
  }

  /**
   * Tichý reconnect na dříve povolené zařízení (per-origin persistentní permission).
   * Vrací true při úspěchu. Při 0 nebo >1 vyhovujících zařízeních nechá výběr na connect().
   */
  async tryReconnect() {
    if (!isWebHidAvailable()) return false;
    try {
      const devices = await navigator.hid.getDevices();
      const match = (devices || []).filter((d) => d.vendorId === DYMO_VENDOR_ID);
      if (match.length !== 1) return false;
      await this._attach(match[0]);
      return true;
    } catch (e) {
      this._lastError = e;
      return false;
    }
  }

  /** Explicitní připojení přes systémový prompt — volat jen z uživatelského gesta. */
  async connect() {
    if (!isWebHidAvailable()) {
      this._setState(ConnectionState.ERROR);
      throw new Error('WebHID není dostupné — vyžaduje Chrome/Edge přes HTTPS. Použijte ruční zadání.');
    }
    this._setState(ConnectionState.CONNECTING);
    try {
      const devices = await navigator.hid.requestDevice({ filters: [{ vendorId: DYMO_VENDOR_ID }] });
      if (!devices || devices.length === 0) {
        this._setState(ConnectionState.DISCONNECTED);
        throw new Error('Nebyla vybrána žádná váha.');
      }
      await this._attach(devices[0]);
    } catch (e) {
      this._lastError = e;
      this._setState(ConnectionState.ERROR);
      throw e;
    }
  }

  async _attach(device) {
    this._device = device;
    if (!device.opened) await device.open();
    if (typeof navigator !== 'undefined' && 'hid' in navigator) {
      navigator.hid.addEventListener('disconnect', this._onDisconnect);
    }
    this._setState(ConnectionState.CONNECTED);
  }

  start() {
    if (this._device) this._device.addEventListener('inputreport', this._onInputReport);
  }

  stop() {
    if (this._device) this._device.removeEventListener('inputreport', this._onInputReport);
  }

  async disconnect() {
    this.stop();
    if (typeof navigator !== 'undefined' && 'hid' in navigator) {
      navigator.hid.removeEventListener('disconnect', this._onDisconnect);
    }
    try {
      if (this._device && this._device.opened) await this._device.close();
    } catch (_e) {
      /* zařízení už mohlo být odpojeno — ignorovat */
    }
    this._device = null;
    this._setState(ConnectionState.DISCONNECTED);
  }

  _handleInputReport(event) {
    try {
      const parsed = parseDymoReport(event.data);
      this._emitReading({ ...parsed, value: parsed.grams, raw: event.data, ts: Date.now() });
    } catch (e) {
      this._lastError = e;
    }
  }

  _handleDisconnect(event) {
    if (this._device && event.device === this._device) {
      this.stop();
      this._device = null;
      this._setState(ConnectionState.DISCONNECTED);
    }
  }
}
